import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserPlus, ShieldCheck, Eye, Trash2, Mail, KeyRound } from "lucide-react";

const ROLES = [
  { value: "admin", label: "Admin", desc: "Full control: manage staff, delete notes" },
  { value: "senior_medic", label: "Senior Medic", desc: "All clinical features" },
  { value: "staff", label: "Staff", desc: "Patients, observations & notes" },
];

const GRADES = [
  { value: "first_aider", label: "First Aider / Medical" },
  { value: "level_3", label: "Level 3" },
  { value: "level_4", label: "Level 4" },
  { value: "technician", label: "Technician (EMT)" },
  { value: "paramedic", label: "Paramedic" },
  { value: "doctor", label: "Doctor" },
];

export default function StaffManagement({ open, onOpenChange }) {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", grade: "first_aider", phone: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const createStaff = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/staff", form);
      toast.success(`${form.name} added as ${ROLES.find((r) => r.value === form.role)?.label}${data.welcome_emailed ? " — welcome email sent" : " — welcome email failed, use Invite to retry"}`);
      setForm({ name: "", email: "", password: "", role: "staff", grade: "first_aider", phone: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const updateRole = async (id, role) => {
    try {
      await api.patch(`/staff/${id}`, { role });
      toast.success("Access level updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (member) => {
    try {
      await api.patch(`/staff/${member.id}`, { active: !member.active });
      toast.success(member.active ? "Access deactivated" : "Access reactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateGrade = async (id, grade) => {
    try {
      await api.patch(`/staff/${id}`, { grade });
      toast.success("Grade updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePhone = async (id, phone) => {
    try {
      await api.patch(`/staff/${id}`, { phone });
      toast.success("Mobile number saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendInvite = async (member) => {
    try {
      const { data } = await api.post(`/staff/${member.id}/send-invite`);
      toast.success(data.message);
      if (!data.emailed && data.temp_password) {
        window.prompt(`Email didn't send — pass this temporary password to ${member.name}:`, data.temp_password);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setPassword = async (member) => {
    const pw = window.prompt(`Set a new password for ${member.name} (min 8 characters).\nIt will be emailed to them and they'll be signed out everywhere.`);
    if (!pw) return;
    try {
      const { data } = await api.post(`/staff/${member.id}/set-password`, { password: pw });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteStaff = async (member) => {
    if (!window.confirm(`Delete ${member.name}? Their login is permanently removed — their name stays on past bookings and records.`)) return;
    try {
      await api.delete(`/staff/${member.id}`);
      toast.success(`${member.name} deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const viewAs = async (member) => {
    try {
      await api.post(`/staff/${member.id}/impersonate`);
      toast.success(`Now viewing as ${member.name}`);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="staff-management-panel">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#0F2547] flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#005EB8]" /> Staff Management
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Create staff logins and set what each person can access.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={createStaff} className="rounded-xl border border-slate-200 bg-[#F4F7FA] p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="create-staff-form">
          <input data-testid="create-staff-name" required placeholder="Full name" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
          <input data-testid="create-staff-email" required type="email" placeholder="Email" value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
          <input data-testid="create-staff-password" required type="text" minLength={8} placeholder="Temporary password (min 8 chars)" value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
          <input data-testid="create-staff-phone" type="tel" placeholder="Mobile number (optional)" value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
          <select data-testid="create-staff-role" value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select data-testid="create-staff-grade" value={form.grade}
            onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white">
            {GRADES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
          <p className="sm:col-span-2 text-xs text-slate-500">{ROLES.find((r) => r.value === form.role)?.desc}</p>
          <button type="submit" disabled={saving} data-testid="create-staff-form-submit"
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60">
            <UserPlus className="h-4 w-4" /> {saving ? "Creating…" : "Create staff login"}
          </button>
        </form>

        <div className="space-y-2">
          {staff.map((m) => (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-2.5" data-testid={`staff-row-${m.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0F2547] break-words">{m.name}</p>
                <p className="text-xs text-slate-500 break-all">{m.email}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={m.role} onChange={(e) => updateRole(m.id, e.target.value)} data-testid={`staff-role-select-${m.id}`}
                  className="w-full h-10 rounded-lg border border-slate-300 px-2 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <select value={m.grade || "first_aider"} onChange={(e) => updateGrade(m.id, e.target.value)} data-testid={`staff-grade-select-${m.id}`}
                  className="w-full h-10 rounded-lg border border-slate-300 px-2 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                  {GRADES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
                <input type="tel" defaultValue={m.phone || ""} placeholder="Mobile number" data-testid={`staff-phone-${m.id}`}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== (m.phone || "")) savePhone(m.id, v); }}
                  className="col-span-2 w-full h-10 rounded-lg border border-slate-300 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => toggleActive(m)} data-testid={`staff-toggle-active-${m.id}`}
                  className={`h-10 px-3 rounded-lg text-xs font-bold border transition-colors ${m.active ? "border-emerald-200 bg-emerald-50 text-[#007F3B]" : "border-red-200 bg-red-50 text-[#D5281B]"}`}>
                  {m.active ? "Active" : "Deactivated"}
                </button>
                <button onClick={() => sendInvite(m)} data-testid={`staff-invite-${m.id}`}
                  className="h-10 px-3 rounded-lg text-xs font-bold border border-slate-300 bg-white text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" /> Invite
                </button>
                <button onClick={() => setPassword(m)} data-testid={`staff-set-password-${m.id}`}
                  className="h-10 px-3 rounded-lg text-xs font-bold border border-slate-300 bg-white text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center gap-1.5">
                  <KeyRound className="h-3.5 w-3.5" /> Password
                </button>
                <button onClick={() => viewAs(m)} data-testid={`staff-view-as-${m.id}`}
                  className="h-10 px-3 rounded-lg text-xs font-bold border border-sky-200 bg-sky-50 text-[#005EB8] hover:bg-sky-100 transition-colors inline-flex items-center gap-1.5">
                  <Eye className="h-3.5 w-3.5" /> View as
                </button>
                <button onClick={() => deleteStaff(m)} data-testid={`staff-delete-${m.id}`}
                  className="h-10 px-3 rounded-lg text-xs font-bold border border-red-200 bg-red-50 text-[#D5281B] hover:bg-red-100 transition-colors inline-flex items-center gap-1.5">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
